import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Key, Eye, EyeSlash, Shuffle, Copy } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-69 — Admin "Set password" dialog.
 *
 * Shared surface for the Employees list + the Affiliate vendor list.
 * Lets an admin (super_admin / manage_employees) rotate any user's
 * password in-band without going through the email reset flow.
 *
 * Guardrails:
 *   - Password ≥ 8 chars (matches backend `admin-set-password`).
 *   - Confirm field must match — prevents a fat-fingered set that
 *     locks the user out with a value the admin can't recall.
 *   - Generate button mints a cryptographically-strong 16-char password
 *     using `crypto.getRandomValues` (only used when actually inserted
 *     via the "Generate" button — the field otherwise starts empty so
 *     the admin's own typed value is never overwritten).
 *   - Copy button + one-time reveal so the admin can hand the new
 *     password to the user securely; after Save the value is wiped
 *     from state and the reveal is force-hidden.
 *   - On success the backend flips `must_change_password: true`, so
 *     the target is forced to rotate on next login regardless of what
 *     the admin chose here.
 *
 * Props:
 *   user       — `{ id, email, name?, role?, org_type? }` (only `id` is
 *                required for the API call; the rest is rendered).
 *   onClose()  — called on backdrop-click, Cancel, ESC, or success.
 *   onSaved?() — optional callback fired after the PATCH resolves;
 *                the parent list can refresh if needed.
 *   testIdPrefix — prefix for every data-testid this dialog emits.
 */
export default function SetPasswordDialog({ user, onClose, onSaved, testIdPrefix = "set-password" }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);

  const tooShort = pw.length > 0 && pw.length < 8;
  const mismatch = confirm.length > 0 && pw !== confirm;
  const canSubmit = pw.length >= 8 && pw === confirm && !busy;

  const generate = () => {
    // 16-char password — mixed case + digits + symbols. Prefer
    // `crypto.getRandomValues` (bias-free because 64 divides 256
    // evenly); fall back to `Math.random` in test / older-browser
    // contexts where the WebCrypto API isn't populated on `window`.
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*";
    const N = 16;
    let vals;
    try {
      const src = (typeof window !== "undefined" && (window.crypto || window.msCrypto)) || null;
      if (src && typeof src.getRandomValues === "function") {
        const bytes = new Uint8Array(N);
        src.getRandomValues(bytes);
        vals = Array.from(bytes, (b) => alphabet[b % alphabet.length]);
      }
    } catch { /* fall through */ }
    if (!vals || vals.some((c) => c === undefined)) {
      vals = Array.from({ length: N },
        () => alphabet[Math.floor(Math.random() * alphabet.length)]);
    }
    const val = vals.join("");
    setPw(val); setConfirm(val); setReveal(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pw);
      toast.success("Password copied to clipboard");
    } catch { toast.error("Clipboard blocked"); }
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post(`/employees/${user.id}/admin-set-password`, {
        new_password: pw,
      });
      toast.success(
        `Password reset for ${user.email || user.name || "user"} — they must change it on next login`,
      );
      // Wipe state on success so the plaintext doesn't survive the modal.
      setPw(""); setConfirm(""); setReveal(false);
      onSaved?.();
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to set password");
    } finally { setBusy(false); }
  };

  const backdropClick = (e) => { if (e.target === e.currentTarget) onClose?.(); };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
      data-testid={`${testIdPrefix}-dialog`}
      onClick={backdropClick}
    >
      <form
        onSubmit={submit}
        className="bg-white border-4 border-black w-full max-w-md brut-shadow-sm"
      >
        <div className="flex items-start justify-between p-4 border-b-2 border-black">
          <div className="flex items-center gap-2">
            <Key size={16} weight="bold" />
            <div>
              <div className="label-mono">Admin action</div>
              <div className="font-chivo font-black text-lg uppercase tracking-tighter leading-none">
                Set new password
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testIdPrefix}-close`}
            className="border border-black p-1 hover:bg-black hover:text-white"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="text-xs">
            <div className="label-mono">Target user</div>
            <div className="font-bold" data-testid={`${testIdPrefix}-target-name`}>
              {user.name || user.email}
            </div>
            <div className="text-[11px] font-mono text-zinc-600" data-testid={`${testIdPrefix}-target-email`}>
              {user.email}
              {user.role && <span className="ml-2 uppercase text-[9px] tracking-widest bg-zinc-200 px-1">{user.role}</span>}
            </div>
          </div>

          <div className="border-2 border-amber-700 bg-amber-50 p-2 text-[11px] text-amber-900 leading-relaxed">
            The user will be forced to change this password on their next
            login. Any active session they have is invalidated immediately.
            The plaintext password is <b>never stored</b> — only a bcrypt hash.
          </div>

          <div>
            <label className="label-mono">New password</label>
            <div className="flex items-stretch gap-2 mt-1">
              <input
                type={reveal ? "text" : "password"}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid={`${testIdPrefix}-new`}
                autoFocus
                autoComplete="new-password"
                className={`flex-1 border ${tooShort ? "border-red-600" : "border-black"} px-3 py-2 font-mono text-[12px]`}
                placeholder="Minimum 8 characters"
              />
              <button
                type="button"
                onClick={() => setReveal((r) => !r)}
                data-testid={`${testIdPrefix}-reveal`}
                className="border border-black px-2 hover:bg-black hover:text-white"
                title={reveal ? "Hide" : "Reveal"}
              >
                {reveal ? <EyeSlash size={12} weight="bold" /> : <Eye size={12} weight="bold" />}
              </button>
              <button
                type="button"
                onClick={generate}
                data-testid={`${testIdPrefix}-generate`}
                className="border border-black px-2 hover:bg-black hover:text-white"
                title="Generate a random 16-char password"
              >
                <Shuffle size={12} weight="bold" />
              </button>
              {pw && (
                <button
                  type="button"
                  onClick={copy}
                  data-testid={`${testIdPrefix}-copy`}
                  className="border border-black px-2 hover:bg-black hover:text-white"
                  title="Copy to clipboard"
                >
                  <Copy size={12} weight="bold" />
                </button>
              )}
            </div>
            {tooShort && (
              <div className="text-[10px] text-red-700 mt-1" data-testid={`${testIdPrefix}-too-short`}>
                Password must be at least 8 characters.
              </div>
            )}
          </div>

          <div>
            <label className="label-mono">Confirm password</label>
            <input
              type={reveal ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid={`${testIdPrefix}-confirm`}
              autoComplete="new-password"
              className={`w-full border ${mismatch ? "border-red-600" : "border-black"} px-3 py-2 mt-1 font-mono text-[12px]`}
            />
            {mismatch && (
              <div className="text-[10px] text-red-700 mt-1" data-testid={`${testIdPrefix}-mismatch`}>
                Passwords don&apos;t match.
              </div>
            )}
          </div>
        </div>

        <div className="border-t-2 border-black px-4 py-3 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testIdPrefix}-cancel`}
            className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid={`${testIdPrefix}-submit`}
            className="border border-black bg-black text-white px-4 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-40"
          >
            {busy ? "Setting…" : "Set password"}
          </button>
        </div>
      </form>
    </div>
  );
}
